import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRutWithoutDots } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { employeeService, type Employee, type ContractStatusFilter } from '@/api/domains/employee';
import { useHoldingCompanies } from '@/api/domains/catalog';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';

// Filters state
interface EmployeeFiltersState {
  companyIds: string[];
  statusFilter: 'V' | 'NV';
  contractStatusFilter: ContractStatusFilter;
}

interface UseEmployeeListResult {
  rows: Employee[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  filters: EmployeeFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<EmployeeFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<Employee>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
}
export function useEmployeeList(): UseEmployeeListResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { companies } = useHoldingCompanies();
  const navigate = useNavigate();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<EmployeeFiltersState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<Employee> | null> => {
      const effectiveCompanyIds = params.filters.companyIds.length === 0
        ? companies.map((company) => String(company.id))
        : params.filters.companyIds;
      const response = await employeeService.list({
        page: params.page,
        pageSize: params.pageSize,
        companyIds: effectiveCompanyIds,
        statusFilter: params.filters.statusFilter,
        contractStatusFilter: params.filters.contractStatusFilter,
        searchText: params.searchText,
        sort: params.sort
          ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
          : undefined,
      }, signal);
      if (!response.success || !response.data) {
        // An aborted request is not an error: navigating away from the list cancels the in-flight
        // fetch, and surfacing it would show a "solicitud cancelada" toast to the user
        if (response.error?.code !== 'ABORTED') {
          showApiError(response);
        }
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [companies, showApiError],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<Employee, EmployeeFiltersState>({
    fetchFn,
    initialFilters: { companyIds: [], statusFilter: 'V', contractStatusFilter: 'ALL' },
    initialSort: { key: 'fullName', direction: 'ASC' },
    orderByMap: {
      rut: 'rut',
      fullName: 'fullName',
      company: 'companyName',
      contract: 'contractNumber',
      status: 'status',
    },
    deduplicate: true,
    enabled: companies.length > 0,
    screenCode: ROUTE_DEFINITIONS.EMPLEADOS.code,
  });

  const companyOptions: SimpleFilter['options'] = useMemo(() => (
    companies.map((company) => ({ value: String(company.id), label: company.name }))
  ), [companies]);

  const contractStatusOptions: SimpleFilter['options'] = useMemo(() => ([
    { value: 'ACTIVE', label: translate('employee:filters.contractStatus.options.active') },
    { value: 'INACTIVE', label: translate('employee:filters.contractStatus.options.inactive') },
    { value: 'ALL', label: translate('employee:filters.contractStatus.options.all') },
  ]), [translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => ([
    {
      key: 'companyIds',
      label: translate('employee:filters.companies.label'),
      multiple: true,
      options: companyOptions,
      placeholder: translate('employee:filters.companies.placeholder'),
    },
    {
      key: 'contractStatusFilter',
      label: translate('employee:filters.contractStatus.label'),
      multiple: false,
      options: contractStatusOptions,
      defaultValue: 'ALL',
      placeholder: translate('employee:filters.contractStatus.label'),
    },
  ]), [companyOptions, contractStatusOptions, translate]);

  const columns: TableColumn<Employee>[] = useMemo(() => [
    { key: 'rut', header: translate('employee:fields.rut'), sortable: true, render: (row: Employee) => formatRutWithoutDots(row.rut) },
    { key: 'fullName', header: translate('employee:fields.fullName'), sortable: true, maxWidth: 350, render: (row: Employee) => (
      <Text
        variant="subheader-medium"
        color={colors.important.main}
        as="span"
        onClick={() => { void navigate(ROUTE_DEFINITIONS.EMPLEADOS_PERFIL.path.replace(':employeeId', String(row.id))); }}
        className="cursor-pointer transition-colors hover:opacity-80"
      >
        {row.fullName}
      </Text>
    ) },
    { key: 'company', header: translate('employee:fields.company'), sortable: true, render: (row: Employee) => row.companyName },
    { key: 'contract', header: translate('employee:fields.contract'), sortable: true, render: (row: Employee) => row.contractNumber },
    { key: 'status', header: translate('employee:fields.statusProfile'), sortable: true, render: (row: Employee) => (
      <Text variant="subheader-regular" as="span">
        {row.status === 'active'
          ? translate('employee:listView.status.active')
          : translate('employee:listView.status.inactive')}
      </Text>
    ) },
  ], [navigate, translate]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    onSearch,
    onSortChange,
    loading,
  };
}

export default useEmployeeList;